import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrashAlt,
  faDownload,
  faFile,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileText,
  faSearch,
  faFilter,
  faSpinner,
  faEye,
  faEyeSlash,
  faUpload,
  faSave,
  faTimes,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { useMessageBox } from '../others/MessageBox';
import {
  getAllFreeDownloads,
  getFreeDownloadCategories,
  createFreeDownload,
  uploadFreeDownload,
  updateFreeDownload,
  deleteFreeDownload
} from '../../services/freeDownloads.util';

const FreeDownloadsManagement = () => {
  const { showMessage } = useMessageBox();
  
  // State management
  const [downloads, setDownloads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    tags: '',
    order: 0,
    isActive: true,
    file: null
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [downloadsResponse, categoriesResponse] = await Promise.all([
        getAllFreeDownloads({ active: undefined }), // Get all downloads including inactive
        getFreeDownloadCategories()
      ]);

      if (downloadsResponse.success) {
        setDownloads(downloadsResponse.data || []);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading free downloads data:', error);
      showMessage('Failed to load free downloads data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filter downloads
  const filteredDownloads = downloads.filter(download => {
    const matchesSearch = download.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         download.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || download.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get file icon
  const getFileIcon = (fileType) => {
    const iconMap = {
      'pdf': faFilePdf,
      'doc': faFileWord,
      'docx': faFileWord,
      'xls': faFileExcel,
      'xlsx': faFileExcel,
      'ppt': faFilePowerpoint,
      'pptx': faFilePowerpoint,
      'txt': faFileText
    };
    return iconMap[fileType] || faFile;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  // Handle upload
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      showMessage('Please select a file to upload', 'error');
      return;
    }

    try {
      setUploading(true);
      
      const uploadData = new FormData();
      uploadData.append('file', formData.file);
      uploadData.append('title', formData.title);
      uploadData.append('description', formData.description);
      uploadData.append('category', formData.category);
      uploadData.append('tags', JSON.stringify(formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)));
      uploadData.append('order', formData.order);
      uploadData.append('isActive', formData.isActive);

      const response = await uploadFreeDownload(uploadData);
      
      if (response.success) {
        showMessage('File uploaded successfully!', 'success');
        setShowUploadModal(false);
        setFormData({
          title: '',
          description: '',
          category: 'general',
          tags: '',
          order: 0,
          isActive: true,
          file: null
        });
        await loadData();
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showMessage(error.message || 'Failed to upload file', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Handle edit
  const handleEdit = (download) => {
    setSelectedDownload(download);
    setFormData({
      title: download.title,
      description: download.description || '',
      category: download.category,
      tags: download.tags?.join(', ') || '',
      order: download.order || 0,
      isActive: download.isActive,
      file: null
    });
    setShowEditModal(true);
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setUpdating(true);
      
      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        order: parseInt(formData.order) || 0,
        isActive: formData.isActive
      };

      const response = await updateFreeDownload(selectedDownload._id, updateData);
      
      if (response.success) {
        showMessage('Download updated successfully!', 'success');
        setShowEditModal(false);
        setSelectedDownload(null);
        await loadData();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (error) {
      console.error('Update error:', error);
      showMessage(error.message || 'Failed to update download', 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete
  const handleDelete = async (download) => {
    if (!window.confirm(`Are you sure you want to delete "${download.title}"?`)) {
      return;
    }

    try {
      const response = await deleteFreeDownload(download._id);
      
      if (response.success) {
        showMessage('Download deleted successfully!', 'success');
        await loadData();
      } else {
        throw new Error(response.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showMessage(error.message || 'Failed to delete download', 'error');
    }
  };

  // Toggle active status
  const toggleActiveStatus = async (download) => {
    try {
      const response = await updateFreeDownload(download._id, {
        isActive: !download.isActive
      });
      
      if (response.success) {
        showMessage(`Download ${!download.isActive ? 'activated' : 'deactivated'} successfully!`, 'success');
        await loadData();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (error) {
      console.error('Toggle status error:', error);
      showMessage(error.message || 'Failed to update status', 'error');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-center items-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-purple-1" />
        <span className="ml-3">Loading free downloads...</span>
      </div>
    );
  }

  return (
    <div className="free-downloads-admin">
      {/* Header */}
      <div className="d-flex justify-between items-center mb-4">
        <div>
          <h2 className="text-22 fw-600 mb-1">Free Downloads Management</h2>
          <p className="text-light-1 text-15">Manage downloadable resources for users</p>
        </div>
        <button
          className="button -sm -purple-1 text-white"
          onClick={() => setShowUploadModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Upload New File
        </button>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="form-group">
            <div className="single-field -search">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Search downloads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <div className="text-14 text-light-1">
            {filteredDownloads.length} of {downloads.length} items
          </div>
        </div>
      </div>

      {/* Downloads List */}
      <div className="rounded-16 border-light overflow-hidden">
        {filteredDownloads.length === 0 ? (
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faFile} size="3x" className="text-light-3 mb-3" />
            <h4 className="text-18 fw-500 mb-2">No downloads found</h4>
            <p className="text-light-1">
              {searchTerm || selectedCategory !== 'all' 
                ? 'Try adjusting your search criteria' 
                : 'Upload your first free download to get started'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead className="bg-light-4">
                <tr>
                  <th className="text-14 fw-600 px-20 py-15">File</th>
                  <th className="text-14 fw-600 px-20 py-15">Title</th>
                  <th className="text-14 fw-600 px-20 py-15">Category</th>
                  <th className="text-14 fw-600 px-20 py-15">Downloads</th>
                  <th className="text-14 fw-600 px-20 py-15">Status</th>
                  <th className="text-14 fw-600 px-20 py-15">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDownloads.map((download) => (
                  <tr key={download._id} className="border-bottom-light">
                    <td className="px-20 py-15">
                      <div className="d-flex items-center">
                        <div className="mr-3">
                          <FontAwesomeIcon
                            icon={getFileIcon(download.fileType)}
                            size="lg"
                            className="text-purple-1"
                          />
                        </div>
                        <div>
                          <div className="text-14 fw-500">
                            {download.fileType?.toUpperCase() || 'FILE'}
                          </div>
                          <div className="text-12 text-light-1">
                            {formatFileSize(download.fileSize)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-20 py-15">
                      <div>
                        <div className="text-15 fw-500 mb-1">{download.title}</div>
                        <div className="text-13 text-light-1">
                          {download.description?.substring(0, 60)}
                          {download.description?.length > 60 && '...'}
                        </div>
                        {download.tags && download.tags.length > 0 && (
                          <div className="mt-2">
                            {download.tags.slice(0, 3).map((tag, index) => (
                              <span key={index} className="badge badge-light mr-1 text-10">
                                {tag}
                              </span>
                            ))}
                            {download.tags.length > 3 && (
                              <span className="text-10 text-light-1">+{download.tags.length - 3} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-20 py-15">
                      <span className="badge badge-outline text-12">
                        {download.category}
                      </span>
                    </td>
                    <td className="px-20 py-15">
                      <div className="text-14 fw-500">
                        <FontAwesomeIcon icon={faDownload} className="mr-1 text-light-1" />
                        {download.downloadCount || 0}
                      </div>
                    </td>
                    <td className="px-20 py-15">
                      <button
                        className={`badge ${download.isActive ? 'badge-success' : 'badge-secondary'} cursor-pointer`}
                        onClick={() => toggleActiveStatus(download)}
                        title={`Click to ${download.isActive ? 'deactivate' : 'activate'}`}
                      >
                        <FontAwesomeIcon
                          icon={download.isActive ? faEye : faEyeSlash}
                          className="mr-1"
                        />
                        {download.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-20 py-15">
                      <div className="d-flex items-center">
                        <button
                          className="button -sm -outline-purple-1 text-purple-1 mr-2"
                          onClick={() => handleEdit(download)}
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="button -sm -outline-red-1 text-red-1"
                          onClick={() => handleDelete(download)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload New Free Download</h5>
                <button
                  className="modal-close"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleUpload}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">File *</label>
                        <input
                          type="file"
                          name="file"
                          className="form-control"
                          onChange={handleInputChange}
                          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                          required
                        />
                        <small className="form-text text-muted">
                          Supported formats: PDF, Word, PowerPoint, Excel, Text files (Max: 50MB)
                        </small>
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="form-group">
                        <label className="form-label">Title *</label>
                        <input
                          type="text"
                          name="title"
                          className="form-control"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">Order</label>
                        <input
                          type="number"
                          name="order"
                          className="form-control"
                          value={formData.order}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Category</label>
                        <input
                          type="text"
                          name="category"
                          className="form-control"
                          value={formData.category}
                          onChange={handleInputChange}
                          placeholder="e.g., study-materials, worksheets"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Tags</label>
                        <input
                          type="text"
                          name="tags"
                          className="form-control"
                          value={formData.tags}
                          onChange={handleInputChange}
                          placeholder="Separate tags with commas"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                          name="description"
                          className="form-control"
                          rows="3"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder="Brief description of the download content"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="isActive"
                          className="form-check-input"
                          id="uploadActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="uploadActive">
                          Make this download immediately available
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="button -outline-dark-1 text-dark-1 mr-3"
                    onClick={() => setShowUploadModal(false)}
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="button -purple-1 text-white"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                        Upload File
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedDownload && (
        <div className="modal-overlay">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Free Download</h5>
                <button
                  className="modal-close"
                  onClick={() => setShowEditModal(false)}
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleUpdate}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="form-group">
                        <label className="form-label">Title *</label>
                        <input
                          type="text"
                          name="title"
                          className="form-control"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">Order</label>
                        <input
                          type="number"
                          name="order"
                          className="form-control"
                          value={formData.order}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Category</label>
                        <input
                          type="text"
                          name="category"
                          className="form-control"
                          value={formData.category}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Tags</label>
                        <input
                          type="text"
                          name="tags"
                          className="form-control"
                          value={formData.tags}
                          onChange={handleInputChange}
                          placeholder="Separate tags with commas"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                          name="description"
                          className="form-control"
                          rows="3"
                          value={formData.description}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="isActive"
                          className="form-check-input"
                          id="editActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="editActive">
                          Make this download available for users
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Current file info */}
                  <div className="alert alert-info mt-3">
                    <div className="d-flex items-center">
                      <FontAwesomeIcon icon={getFileIcon(selectedDownload.fileType)} className="mr-2" />
                      <div>
                        <strong>Current file:</strong> {selectedDownload.fileType?.toUpperCase() || 'FILE'}
                        {selectedDownload.fileSize && (
                          <span className="ml-2">({formatFileSize(selectedDownload.fileSize)})</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="button -outline-dark-1 text-dark-1 mr-3"
                    onClick={() => setShowEditModal(false)}
                    disabled={updating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="button -purple-1 text-white"
                    disabled={updating}
                  >
                    {updating ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .modal-dialog {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          padding: 20px 30px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: between;
          align-items: center;
        }

        .modal-title {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          flex: 1;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close:hover {
          color: #374151;
        }

        .modal-body {
          padding: 30px;
        }

        .modal-footer {
          padding: 20px 30px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          z-index: 1;
        }

        .single-field.-search {
          position: relative;
        }

        .single-field.-search input {
          padding-left: 40px;
        }

        .table th {
          background-color: #f8fafc;
          font-weight: 600;
          color: #374151;
        }

        .badge {
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 6px;
        }

        .badge-success {
          background-color: #d1fae5;
          color: #065f46;
        }

        .badge-secondary {
          background-color: #f3f4f6;
          color: #6b7280;
        }

        .badge-light {
          background-color: #f3f4f6;
          color: #374151;
        }

        .badge-outline {
          background-color: transparent;
          border: 1px solid #d1d5db;
          color: #6b7280;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid;
        }

        .alert-info {
          background-color: #dbeafe;
          border-color: #93c5fd;
          color: #1e40af;
        }
      `}</style>
    </div>
  );
};

export default FreeDownloadsManagement;
